import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Spinner,
} from '@/components/ui';
import {
  getProbationCase,
  getProbationPolicy,
  type ProbationCase,
} from '@/api/services/probation.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { ProbationStatusBadge } from './ProbationStatusBadge';
import {
  ConfirmForm,
  EndForm,
  ExtendForm,
  ReviewForm,
  type ProbationAction,
} from './ProbationActionForms';
import {
  daysUntil,
  effectiveLimits,
  formatDate,
  isActionable,
  normalizeCase,
  normalizePolicy,
  RECOMMENDATION_LABELS,
  relativeEnd,
} from './probationFormat';

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</h3>
  );
}

function ReviewSummary({ probationCase }: { probationCase: ProbationCase }): React.ReactElement {
  const { review } = probationCase;
  if (!review) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-300">No review has been submitted yet.</p>
    );
  }
  return (
    <dl className="grid gap-x-6 gap-y-4 rounded-xl border border-gray-200 p-4 sm:grid-cols-2 dark:border-gray-700">
      <Field label="Rating">{review.rating ? `${review.rating} / 5` : '—'}</Field>
      <Field label="Recommendation">{RECOMMENDATION_LABELS[review.recommendation]}</Field>
      <div className="sm:col-span-2">
        <Field label="Strengths">
          <span className="whitespace-pre-line break-words">{review.strengths || '—'}</span>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Areas to improve">
          <span className="whitespace-pre-line break-words">{review.improvements || '—'}</span>
        </Field>
      </div>
      <Field label="Submitted">{formatDate(review.submittedAt)}</Field>
    </dl>
  );
}

function ExtensionHistory({ probationCase }: { probationCase: ProbationCase }): React.ReactElement {
  if (probationCase.extensions.length === 0) {
    return <p className="text-sm text-gray-600 dark:text-gray-300">Not extended.</p>;
  }
  return (
    <ol className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
      {probationCase.extensions.map((extension, index) => (
        <li key={`${extension.decidedAt}-${index}`} className="space-y-1 p-3">
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-gray-900 dark:text-gray-100">
            <span className="text-gray-500 dark:text-gray-400">
              {formatDate(extension.previousEndDate)}
            </span>
            <span aria-hidden className="text-gray-400">→</span>
            <span className="sr-only">extended to</span>
            <span className="font-medium">{formatDate(extension.newEndDate)}</span>
          </p>
          {extension.reason && (
            <p className="break-words text-sm text-gray-700 dark:text-gray-200">
              {extension.reason}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Decided {formatDate(extension.decidedAt)}
          </p>
        </li>
      ))}
    </ol>
  );
}

/** Why an action is unavailable, shown next to its disabled button. */
function Blocked({ id, children }: { id: string; children: React.ReactNode }): React.ReactElement {
  return (
    <p id={id} className="text-xs text-gray-600 dark:text-gray-300">
      {children}
    </p>
  );
}

/**
 * Case drawer: dates, review, extension history, decision, and the commands
 * the case still accepts. Client checks mirror the policy for early feedback;
 * the server remains the authority and its refusals show in the open form.
 */
export function ProbationCaseDialog({
  companyId,
  caseId,
  onClose,
  reviewerOnly = false,
}: {
  companyId: string;
  caseId: string;
  onClose: () => void;
  /**
   * The reporting manager's view: review only. The policy read and HR
   * decisions need `hr:probation`, so they are neither fetched nor offered.
   */
  reviewerOnly?: boolean;
}): React.ReactElement {
  const [action, setAction] = useState<ProbationAction | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-probation-case', companyId, caseId],
    queryFn: () => getProbationCase(companyId, caseId),
  });
  const { data: policyData } = useQuery({
    queryKey: ['hr-probation-policy', companyId],
    queryFn: () => getProbationPolicy(companyId),
    enabled: !reviewerOnly,
  });

  const probationCase = normalizeCase(data);
  const policy = policyData ? normalizePolicy(policyData, companyId) : undefined;

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-10" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">Loading probation case</span>
      </div>
    );
  } else if (error) {
    body = (
      <div className="space-y-3" role="alert">
        <p className="text-sm text-danger">{getErrorMessage(error)}</p>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  } else if (!probationCase) {
    body = (
      <p className="text-sm text-gray-600 dark:text-gray-300">
        This probation case could not be displayed.
      </p>
    );
  } else {
    const limits = effectiveLimits(probationCase, policy);
    const actionable = isActionable(probationCase.status);
    const days = daysUntil(probationCase.currentEndDate);
    const relative = actionable ? relativeEnd(days) : null;
    const extensionsUsed = probationCase.extensions.length;
    const maxReached = limits.maxExtensions !== null && extensionsUsed >= limits.maxExtensions;
    const needsReview = limits.reviewRequired && !probationCase.review;
    const done = (): void => setAction(null);

    body = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <ProbationStatusBadge status={probationCase.status} />
          {probationCase.employee.email && (
            <span className="text-xs text-gray-600 dark:text-gray-300">
              {probationCase.employee.email}
            </span>
          )}
        </div>

        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field label="Start">{formatDate(probationCase.startDate)}</Field>
          <Field label="Original end">{formatDate(probationCase.originalEndDate)}</Field>
          <Field label="Current end">
            {formatDate(probationCase.currentEndDate)}
            {relative && (
              <span
                className={
                  days !== null && days < 0
                    ? 'block text-xs font-medium text-danger'
                    : 'block text-xs text-gray-600 dark:text-gray-300'
                }
              >
                {relative}
              </span>
            )}
          </Field>
        </dl>

        <section>
          <SectionHeading>Manager review</SectionHeading>
          <ReviewSummary probationCase={probationCase} />
        </section>

        <section>
          <SectionHeading>
            Extensions
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {limits.maxExtensions !== null
                ? `${extensionsUsed} of ${limits.maxExtensions} used`
                : `${extensionsUsed} used`}
            </span>
          </SectionHeading>
          <ExtensionHistory probationCase={probationCase} />
        </section>

        {probationCase.decision && (
          <section>
            <SectionHeading>Decision</SectionHeading>
            <dl className="grid gap-x-6 gap-y-4 rounded-xl border border-gray-200 p-4 sm:grid-cols-3 dark:border-gray-700">
              <Field label="Outcome">
                {probationCase.decision.outcome === 'confirmed' ? 'Confirmed' : 'Ended'}
              </Field>
              <Field label="Effective">{formatDate(probationCase.decision.effectiveDate)}</Field>
              <Field label="Decided">{formatDate(probationCase.decision.decidedAt)}</Field>
              {probationCase.decision.reason && (
                <div className="sm:col-span-3">
                  <Field label="Reason">
                    <span className="break-words">{probationCase.decision.reason}</span>
                  </Field>
                </div>
              )}
            </dl>
          </section>
        )}

        {actionable && (
          <section className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <SectionHeading>Actions</SectionHeading>
            {action === null && reviewerOnly ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => setAction('review')}>
                  {probationCase.review ? 'Replace review' : 'Submit review'}
                </Button>
              </div>
            ) : action === null ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setAction('review')}>
                    {probationCase.review ? 'Replace review' : 'Submit review'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={maxReached}
                    aria-describedby={maxReached ? 'probation-extend-blocked' : undefined}
                    onClick={() => setAction('extend')}
                  >
                    Extend
                  </Button>
                  <Button
                    size="sm"
                    disabled={needsReview}
                    aria-describedby={needsReview ? 'probation-confirm-blocked' : undefined}
                    onClick={() => setAction('confirm')}
                  >
                    Confirm
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setAction('end')}>
                    End probation
                  </Button>
                </div>
                {maxReached && (
                  <Blocked id="probation-extend-blocked">
                    Extension limit reached ({limits.maxExtensions} allowed by policy).
                  </Blocked>
                )}
                {needsReview && (
                  <Blocked id="probation-confirm-blocked">
                    Policy requires a manager review before confirmation.
                  </Blocked>
                )}
              </div>
            ) : action === 'review' ? (
              <ReviewForm companyId={companyId} probationCase={probationCase} onDone={done} />
            ) : action === 'extend' ? (
              <ExtendForm
                companyId={companyId}
                probationCase={probationCase}
                maxExtensionDays={limits.maxExtensionDays}
                onDone={done}
              />
            ) : action === 'confirm' ? (
              <ConfirmForm companyId={companyId} probationCase={probationCase} onDone={done} />
            ) : (
              <EndForm companyId={companyId} probationCase={probationCase} onDone={done} />
            )}
          </section>
        )}
      </>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[94dvh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto p-0">
        <div className="border-b border-gray-200 px-5 py-4 pr-14 dark:border-gray-700">
          <DialogTitle>{probationCase?.employee.name ?? 'Probation case'}</DialogTitle>
          <DialogDescription className="mt-1">Probation case</DialogDescription>
        </div>
        <div className="space-y-5 p-5">{body}</div>
      </DialogContent>
    </Dialog>
  );
}
